'use client'

import * as React from 'react'
import {
  CaretSortIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@radix-ui/react-icons'
import { Select as BaseSelect } from '@base-ui/react/select'

import { cn } from '@/lib/utils'

interface SelectProps
  extends Omit<React.ComponentProps<typeof BaseSelect.Root>, 'onValueChange'> {
  /** Narrowed from Base UI `unknown` — select values are always strings. */
  onValueChange?: (value: string) => void
}

/**
 * Adapts string handlers to Base UI's `onValueChange(value: unknown, …)`.
 * Use when composing handlers outside `Select`.
 */
export function onSelectStringValue(handler: (value: string) => void) {
  return (value: unknown) => {
    if (typeof value === 'string') {
      handler(value)
    }
  }
}

/**
 * Matches prior Radix behavior: no modal scroll-lock / full-screen overlay by default.
 * Pass `modal` to opt into Base UI modal behavior.
 */
function Select({ modal, onValueChange, ...rest }: SelectProps) {
  return (
    <BaseSelect.Root
      modal={modal ?? false}
      onValueChange={
        onValueChange
          ? (value) => {
              if (typeof value === 'string') {
                onValueChange(value)
              }
            }
          : undefined
      }
      {...rest}
    />
  )
}

const SelectGroup = BaseSelect.Group

const SelectValue = BaseSelect.Value

const SelectTrigger = React.forwardRef<
  React.ComponentRef<typeof BaseSelect.Trigger>,
  React.ComponentPropsWithoutRef<typeof BaseSelect.Trigger>
>(({ className, children, ...props }, ref) => (
  <BaseSelect.Trigger
    ref={ref}
    className={cn(
      'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      className,
    )}
    {...props}
  >
    {children}
    <BaseSelect.Icon>
      <CaretSortIcon className="h-4 w-4 opacity-50" />
    </BaseSelect.Icon>
  </BaseSelect.Trigger>
))
SelectTrigger.displayName = 'SelectTrigger'

const SelectScrollUpButton = React.forwardRef<
  React.ComponentRef<typeof BaseSelect.ScrollUpArrow>,
  React.ComponentPropsWithoutRef<typeof BaseSelect.ScrollUpArrow>
>(({ className, ...props }, ref) => (
  <BaseSelect.ScrollUpArrow
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className,
    )}
    {...props}
  >
    <ChevronUpIcon />
  </BaseSelect.ScrollUpArrow>
))
SelectScrollUpButton.displayName = 'SelectScrollUpButton'

const SelectScrollDownButton = React.forwardRef<
  React.ComponentRef<typeof BaseSelect.ScrollDownArrow>,
  React.ComponentPropsWithoutRef<typeof BaseSelect.ScrollDownArrow>
>(({ className, ...props }, ref) => (
  <BaseSelect.ScrollDownArrow
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className,
    )}
    {...props}
  >
    <ChevronDownIcon />
  </BaseSelect.ScrollDownArrow>
))
SelectScrollDownButton.displayName = 'SelectScrollDownButton'

export interface SelectContentProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof BaseSelect.Popup>,
    'children'
  >,
    Pick<
      React.ComponentPropsWithoutRef<typeof BaseSelect.Positioner>,
      'side' | 'align' | 'sideOffset' | 'alignOffset'
    > {
  children?: React.ReactNode
  /** @default 'popper' — maps to Base UI `alignItemWithTrigger` */
  position?: 'popper' | 'item'
}

const SelectContent = React.forwardRef<HTMLDivElement, SelectContentProps>(
  (
    {
      className,
      children,
      position = 'popper',
      side = 'bottom',
      align = 'center',
      sideOffset,
      alignOffset,
      ...props
    },
    ref,
  ) => (
    <BaseSelect.Portal>
      <BaseSelect.Backdrop className="pointer-events-none fixed inset-0 z-120 bg-transparent" />
      <BaseSelect.Positioner
        alignItemWithTrigger={position === 'popper'}
        className="z-120"
        side={side}
        align={align}
        sideOffset={sideOffset ?? (position === 'popper' ? 4 : 0)}
        alignOffset={alignOffset}
      >
        <SelectScrollUpButton />
        <BaseSelect.Popup
          ref={ref}
          className={cn(
            'relative max-h-96 min-w-32 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md',
            'data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95',
            position === 'popper' &&
              'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
            className,
          )}
          {...props}
        >
          <BaseSelect.Arrow className="fill-popover stroke-border data-[side=none]:hidden" />
          <BaseSelect.List
            className={cn(
              'max-h-[inherit] overflow-y-auto p-1',
              position === 'popper' && 'min-w-(--anchor-width)',
            )}
          >
            {children}
          </BaseSelect.List>
        </BaseSelect.Popup>
        <SelectScrollDownButton />
      </BaseSelect.Positioner>
    </BaseSelect.Portal>
  ),
)
SelectContent.displayName = 'SelectContent'

/** Group heading inside the list (was Radix `Select.Label` in dropdown groups). */
const SelectLabel = React.forwardRef<
  React.ComponentRef<typeof BaseSelect.GroupLabel>,
  React.ComponentPropsWithoutRef<typeof BaseSelect.GroupLabel>
>(({ className, ...props }, ref) => (
  <BaseSelect.GroupLabel
    ref={ref}
    className={cn('px-2 py-1.5 text-sm font-semibold', className)}
    {...props}
  />
))
SelectLabel.displayName = 'SelectLabel'

const SelectItem = React.forwardRef<
  React.ComponentRef<typeof BaseSelect.Item>,
  React.ComponentPropsWithoutRef<typeof BaseSelect.Item>
>(({ className, children, ...props }, ref) => (
  <BaseSelect.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-highlighted:bg-accent data-highlighted:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute right-2 flex size-4 items-center justify-center">
      <BaseSelect.ItemIndicator>
        <CheckIcon className="size-4 overflow-visible" />
      </BaseSelect.ItemIndicator>
    </span>
    <BaseSelect.ItemText>{children}</BaseSelect.ItemText>
  </BaseSelect.Item>
))
SelectItem.displayName = 'SelectItem'

const SelectSeparator = React.forwardRef<
  React.ComponentRef<typeof BaseSelect.Separator>,
  React.ComponentPropsWithoutRef<typeof BaseSelect.Separator>
>(({ className, ...props }, ref) => (
  <BaseSelect.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
))
SelectSeparator.displayName = 'SelectSeparator'

export {
  Select,
  type SelectProps,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
